import React, { useState, useRef } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Download,
  Eye,
  FileDown,
  Handshake,
  Loader2,
  Mail,
  Paperclip,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserRound,
} from "lucide-react";
import { message, Modal, Pagination, Alert } from "antd";
import ChipDipCollection from "../component/ChipDipCollection";
import GenericTable from "../component/GenericTable";
import DetailOrders from "../component/DetailOrders";
import DetailOffer from "../component/DetailOffer";
import OrderOffersModal from "../component/OrderOffersModal";
import OriginalDocumentPreview from "../component/OriginalDocumentPreview";
import { fetchEmployees } from "../api/employeesApi";
import { fetchOrders, sendOrderEmail } from "../api/ordersApi";
import styles from "./NewAnalysisPage.module.css";

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
const toNumber = (value) => {
  const parsed = Number(
    String(value ?? "")
      .replace(/\s/g, "")
      .replace(",", ".")
      .replace(/[^\d.-]/g, "")
  );
  return Number.isFinite(parsed) ? parsed : 0;
};

async function postForm(url, field, value) {
  const form = new FormData();
  form.append(field, value);
  const response = await fetch(url, { method: "POST", body: form });
  if (!response.ok) throw new Error(`Ошибка сервера: ${response.status}`);
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

function analyse(requestRows, offerGroups) {
  const offerIndex = new Map();
  offerGroups.forEach(({ document, rows }) =>
    rows.forEach((row) => {
      const key = normalize(row.name ?? row.part_number ?? row.partNumber ?? row.clean_part_number);
      const price = toNumber(row.price);
      if (!key || price <= 0) return;
      const item = {
        supplier: document.name,
        price,
        priceText: normalizePriceText(row.price),
        leadTime: row.lt ?? row.lead_time ?? row.leadTime ?? "—",
        shelfLife:
          row.dc ?? row.date_code ?? row.dateCode ?? row.shelf_life ?? row.shelfLife ?? "—",
      };
      offerIndex.set(key, [...(offerIndex.get(key) || []), item]);
    })
  );
  let savings = 0;
  const wins = {};
  const matches = requestRows
    .map((row, index) => {
      const part = String(
        row.name ?? row.part_number ?? row.partNumber ?? row.clean_part_number ?? ""
      ).trim();
      const qty = Math.max(1, toNumber(row.cnt ?? row.qty ?? row.quantity) || 1);
      const candidates = (offerIndex.get(normalize(part)) || []).sort((a, b) => a.price - b.price);
      const best = candidates[0];
      const highest = candidates[candidates.length - 1];
      const worst = best && highest && best.price !== highest.price ? highest : null;
      if (best) {
        savings += Math.max(0, ((highest?.price || best.price) - best.price) * qty);
        wins[best.supplier] = (wins[best.supplier] || 0) + 1;
      }
      return {
        index: index + 1,
        part,
        qty,
        best,
        worst,
        offers: candidates,
        count: candidates.length,
      };
    })
    .filter((item) => item.part);
  const matched = matches.filter((item) => item.best).length;
  const leader = Object.entries(wins).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
  return {
    matches,
    matched,
    savings,
    leader,
    coverage: matches.length ? Math.round((matched / matches.length) * 100) : 0,
  };
}

export default function NewAnalysisPage({
  session,
  showMyOrders,
  showMyOffers,
  onToggleMyOrders,
  onToggleMyOffers,
  orders = [],
  offers = [],
  catalogLinks = false,
  orderPage, offerPage,
  setOrders,
  setOffers,
}) {
  const [orderId, setOrderId] = useState("");
  const [offerIds, setOfferIds] = useState([]);
  const [documentTab, setDocumentTab] = useState("orders");
  const [status, setStatus] = useState("idle");
  const [stage, setStage] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [offersOrder, setOffersOrder] = useState(null);
  const [emailOrder, setEmailOrder] = useState(null);
  const [selectionDownload, setSelectionDownload] = useState("");
  const documentCache = useRef({orders:new Map(),offers:new Map()});
  orders.forEach(item => documentCache.current.orders.set(String(item.id), item));
  offers.forEach(item => documentCache.current.offers.set(String(item.id), item));
  const selectedOrder = Array.from(documentCache.current.orders.values()).find((item) => String(item.id) === String(orderId));
  const selectedOffers = Array.from(documentCache.current.offers.values()).filter((item) =>
    offerIds.some((id) => String(id) === String(item.id))
  );
  const stages = [
    "Загружаю запрос из базы",
    "Получаю предложения",
    "Сопоставляю позиции",
    "Формирую рекомендации",
  ];
  const generateFromSelection = async (url, kind) => {
    setSelectionDownload(kind);
    try {
      await downloadGeneratedReport(
        url,
        kind,
        offerIds,
        orderId,
        selectedOrder?.name || "analysis"
      );
    } catch {
      message.error("Не удалось сформировать файл");
    } finally {
      setSelectionDownload("");
    }
  };

  const run = async () => {
    setStatus("processing");
    setStage(0);
    setError("");
    setResult(null);
    try {
      const requestRows = await postForm("/api/detailOrder", "orders", orderId);
      setStage(1);
      const offerGroups = await Promise.all(
        selectedOffers.map(async (document) => ({
          document,
          rows: await postForm("/api/listFileOfferModal", "offers", document.id),
        }))
      );
      setStage(2);
      const analysis = analyse(requestRows, offerGroups);
      await new Promise((resolve) => setTimeout(resolve, 350));
      setStage(3);
      const unmatchedParts = Array.from(
        new Map(
          analysis.matches
            .filter((row) => !row.best && String(row.part || "").trim())
            .map((row) => [normalize(row.part), String(row.part).trim()])
        ).values()
      );
      const recentOffers = await fetchRecentOffersBatch(unmatchedParts, 3);
      analysis.matches = analysis.matches.map((row) =>
        row.best
          ? row
          : {
              ...row,
              externalOffers: recentOffers.offers.get(normalize(row.part)) || [],
              externalLookupFailed: recentOffers.failed,
            }
      );
      await new Promise((resolve) => setTimeout(resolve, 350));
      setResult(analysis);
      setStatus("done");
    } catch (e) {
      setError(e.message || "Не удалось получить документы из базы");
      setStatus("idle");
    }
  };
  const reset = () => {
    setDocumentTab("orders");
    setOrderId("");
    setOfferIds([]);
    setResult(null);
    setStatus("idle");
    setError("");
  };
  const backToSelection = () => {
    setResult(null);
    setStatus("idle");
    setError("");
  };

  return (
    <main className={styles.page}>
      {!result && status !== "processing" && (
        <section className={styles.workspace}>
          <div className={styles.actionPanel}>
            <div className={styles.actionIcon}>
              <Sparkles size={25} />
            </div>
            <strong>Готово к анализу</strong>
            <span>
              {selectedOrder ? 1 : 0} запрос · {selectedOffers.length} предложений
            </span>
            <div className={styles.analysisActionButtons}>
              <button
                className={styles.secondaryAnalysisAction}
                disabled={!selectedOrder || !selectedOffers.length || Boolean(selectionDownload)}
                onClick={() => generateFromSelection("/api/generateReport", "comparison")}
              >
                <FileDown size={16} />
                {selectionDownload === "comparison" ? "Формирование…" : "Сравнение цен"}
              </button>
              <button
                className={styles.secondaryAnalysisAction}
                disabled={!selectedOrder || !selectedOffers.length || Boolean(selectionDownload)}
                onClick={() => generateFromSelection("/api/generateBestOffer", "best-offer")}
              >
                <Sparkles size={16} />
                {selectionDownload === "best-offer" ? "Формирование…" : "Лучшее предложение"}
              </button>
              <button
                className={styles.runAnalysisAction}
                disabled={!selectedOrder || !selectedOffers.length}
                onClick={run}
              >
                Запустить анализ <ArrowRight size={17} />
              </button>
            </div>
          </div>
          <div className={styles.documentTabs} role="tablist" aria-label="Документы для анализа">
            {[
              { id: "orders", label: "Заказы", count: orderPage?.total ?? orders.length },
              { id: "offers", label: "Предложения", count: offerPage?.total ?? offers.length },
            ].map(({ id, label, count }) => (
              <button
                key={id}
                type="button"
                role="tab"
                id={`document-tab-${id}`}
                aria-controls={`document-panel-${id}`}
                aria-selected={documentTab === id}
                tabIndex={documentTab === id ? 0 : -1}
                onClick={() => setDocumentTab(id)}
                onKeyDown={(event) => {
                  if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                  event.preventDefault();
                  const next = event.key === "Home" ? "orders"
                    : event.key === "End" ? "offers"
                    : id === "orders" ? "offers" : "orders";
                  setDocumentTab(next);
                  document.getElementById(`document-tab-${next}`)?.focus();
                }}
              >
                {label} <span>{count}</span>
              </button>
            ))}
          </div>
          <DocumentTable
            tabId="orders"
            mobileActive={documentTab === "orders"}
            icon={<ClipboardList />}
            title="Запросы"
            hint="Документы с запросами заказчиков"
            showMine={showMyOrders}
            onToggleMine={() => {
              setOrderId("");
              onToggleMyOrders();
            }}
            page={orderPage}
            items={orders}
            selected={orderId ? [orderId] : []}
            onChange={(keys) => setOrderId(keys.length ? keys[keys.length - 1] : "")}
            onDetail={(item) => setDetail({ type: "order", item })}
            onOffers={(item) => setOffersOrder(item)}
            onEmail={(item) => setEmailOrder(item)}
            onDownload={downloadDocument}
            onDelete={(item) => deleteDocument("order", item, setOrders, () => { if(String(orderId) === String(item.id)) setOrderId(""); })}
          />
          <DocumentTable
            tabId="offers"
            mobileActive={documentTab === "offers"}
            icon={<Handshake />}
            title="Предложения"
            hint="Коммерческие предложения поставщиков"
            showMine={showMyOffers}
            onToggleMine={() => {
              setOfferIds([]);
              onToggleMyOffers();
            }}
            page={offerPage}
            items={offers}
            selected={offerIds}
            onChange={setOfferIds}
            multiple
            onDetail={(item) => setDetail({ type: "offer", item })}
            onDownload={downloadDocument}
            onDelete={(item) => deleteDocument("offer", item, setOffers, async () => {
              // [[Order Offer Links]] Refresh server counts after deleting a linked proposal.
              setOfferIds((ids) => ids.filter((id) => String(id) !== String(item.id)));
              if (orderPage) orderPage.refresh();
              else setOrders?.(await fetchOrders({ mine: showMyOrders }));
            })}
          />
        </section>
      )}
      {error && <div className={styles.error}>{error}</div>}
      {status === "processing" && <Processing stages={stages} stage={stage} />}
      {result && (
        <Results
          catalogLinks={catalogLinks}
          result={result}
          orderId={orderId}
          offerIds={offerIds}
          orderName={selectedOrder?.name || "analysis"}
          onBack={backToSelection}
          onReset={reset}
        />
      )}
      <DetailOrders
        catalogLinks={catalogLinks}
        isOpen={detail?.type === "order"}
        selectedOrderFileId={detail?.item?.id}
        numberOffer={detail?.item?.name}
        onClose={() => setDetail(null)}
        onOpenOriginal={() => {
          setPreview(detail);
          setDetail(null);
        }}
      />
      <OriginalDocumentPreview
        open={Boolean(preview)}
        document={preview?.item}
        type={preview?.type}
        onClose={() => setPreview(null)}
        onOpenDetails={() => {
          setDetail(preview);
          setPreview(null);
        }}
      />
      <DetailOffer
        catalogLinks={catalogLinks}
        isOpen={detail?.type === "offer"}
        selectedFileIds={detail?.item?.id ? [detail.item.id] : []}
        numberOffer={detail?.item?.name}
        onClose={() => setDetail(null)}
        onOpenOriginal={() => {
          setPreview(detail);
          setDetail(null);
        }}
      />
      <OrderOffersModal
        catalogLinks={catalogLinks}
        open={Boolean(offersOrder)}
        order={offersOrder}
        onClose={() => { setOffersOrder(null); orderPage?.refresh(); offerPage?.refresh(); }}
        setOffers={setOffers}
      />
      <OrderEmailModal
        order={emailOrder}
        employeeName={session?.employeeName}
        onClose={() => setEmailOrder(null)}
      />
    </main>
  );
}

function DocumentTable({
  tabId,
  page,
  mobileActive,
  icon,
  title,
  hint,
  showMine,
  onToggleMine,
  items,
  selected,
  onChange,
  multiple,
  onDetail,
  onOffers,
  onEmail,
  onDownload,
  onDelete,
}) {
  const isItemSelected = (item) =>
    selected.some((id) => String(id) === String(item.id));
  const toggleMobileSelection = (item) => {
    if (!multiple) {
      onChange([item.id]);
      return;
    }

    onChange(
      isItemSelected(item)
        ? selected.filter((id) => String(id) !== String(item.id))
        : [...selected, item.id]
    );
  };
  const linkedCount = (item) =>
    item.linked_offers_count ?? item.linkedOffersCount ?? 0;
  const columns = [
    { title: "ID", dataIndex: "id", key: "id", sortable: true, width: 70 },
    {
      title: "Сотрудник",
      dataIndex: "employee_name",
      key: "employee_name",
      sortable: true,
      searchable: true,
      width: 130,
    },
    {
      title: multiple ? "Поставщик" : "Заказчик",
      dataIndex: "name",
      key: "name",
      sortable: true,
      searchable: true,
    },
    {
      title: "Дата загрузки",
      dataIndex: "created",
      key: "created",
      sortable: true,
      dateFilter: true,
      width: 130,
      render: formatDate,
    },
    {
      title: "Действия",
      key: "actions",
      width: multiple ? 128 : 240,
      render: (_, item) => (
        <div className={styles.rowActions}>
          {!multiple && <ChipDipCollection orderId={item.id} />}
          <button title="Детализация" onClick={() => onDetail(item)}>
            <Eye size={15} />
          </button>
          {!multiple && (
            <button
              title="Отправить запрос по почте"
              aria-label="Отправить запрос по почте"
              onClick={() => onEmail(item)}
            >
              <Mail size={15} />
            </button>
          )}
          {!multiple && (
            <button
              title="Связанные предложения / загрузить новое"
              aria-label="Связанные предложения / загрузить новое"
              onClick={() => onOffers(item)}
            >
              <Paperclip size={15} />
              <span
                className={`${styles.linkCount} ${
                  (item.linked_offers_count ?? item.linkedOffersCount ?? 0) === 0
                    ? styles.linkCountEmpty
                    : ""
                }`}
              >
                {item.linked_offers_count ?? item.linkedOffersCount ?? 0}
              </span>
            </button>
          )}
          <button
            title="Скачать файл"
            onClick={() => onDownload(multiple ? "offer" : "order", item)}
          >
            <Download size={15} />
          </button>
          <button className={styles.deleteAction} title="Удалить" onClick={() => onDelete(item)}>
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];
  return (
    <section
      id={`document-panel-${tabId}`}
      role="tabpanel"
      aria-labelledby={`document-tab-${tabId}`}
      tabIndex={0}
      className={`${styles.tablePicker} ${mobileActive ? "" : styles.mobilePanelHidden}`}
    >
      <div className={styles.pickerHeader}>
        <div className={styles.cardHeading}>
          {icon}
          <div>
            <strong>{title}</strong>
            <span>{hint}</span>
          </div>
        </div>
        <div className={styles.pickerHeaderActions}>
          <button
            type="button"
            className={`${styles.mineFilter} ${showMine ? styles.mineFilterActive : ""}`}
            onClick={onToggleMine}
            aria-pressed={showMine}
            title={showMine ? `Показать все ${title.toLowerCase()}` : `Показать мои ${title.toLowerCase()}`}
          >
            <UserRound size={15} />
            {showMine ? "Только мои" : "Мои документы"}
          </button>
          <span className={styles.pickerCount}>{page?.total ?? items.length}</span>
        </div>
      </div>
      {page?.error && <Alert type="error" message={page.error} action={<button onClick={page.refresh}>Повторить</button>}/>}
      <div className={styles.pickerTable}>
        <GenericTable
          serverState={page?.params}
          onServerChange={page?.change}
          loading={page?.loading}
          data={items}
          columnsConfig={columns}
          rowKey="id"
          scroll={{ x: 620 }}
          rowSelection={{
            type: multiple ? "checkbox" : "radio",
            preserveSelectedRowKeys: true,
            selectedRowKeys: selected,
            onChange: (keys) => onChange(keys),
          }}
        />
      </div>
      {page && <Pagination style={{padding:12}} current={page.params.page+1} pageSize={page.params.size} total={page.total} showSizeChanger pageSizeOptions={[10,25,50,100]} onChange={(number,size)=>page.change({page:size!==page.params.size?0:number-1,size})} />}
      <div className={styles.mobileDocumentList}>
        {items.map((item) => (
          <article
            key={item.id}
            className={`${styles.mobileDocumentCard} ${
              isItemSelected(item) ? styles.mobileDocumentSelected : ""
            }`}
          >
            <button
              type="button"
              className={styles.mobileDocumentSelect}
              onClick={() => toggleMobileSelection(item)}
              aria-label={isItemSelected(item) ? "Снять выбор" : "Выбрать документ"}
            >
              <span className={multiple ? styles.mobileCheckbox : styles.mobileRadio}>
                {isItemSelected(item) ? "✓" : ""}
              </span>
              <span>
                <strong>{item.name}</strong>
                <small>ID {item.id} · {formatDate(item.created)}</small>
              </span>
            </button>

            <div className={styles.mobileDocumentMeta}>
              <span>Сотрудник</span>
              <strong>{item.employee_name || item.employeeName || "—"}</strong>
            </div>

            <div className={styles.mobileDocumentActions}>
              {!multiple && <ChipDipCollection orderId={item.id} />}
              <button title="Детализация" onClick={() => onDetail(item)}>
                <Eye size={17} />
              </button>
              {!multiple && (
                <button title="Отправить запрос по почте" onClick={() => onEmail(item)}>
                  <Mail size={17} />
                </button>
              )}
              {!multiple && (
                <button title="Связанные предложения" onClick={() => onOffers(item)}>
                  <Paperclip size={17} />
                  <span
                    className={`${styles.linkCount} ${
                      linkedCount(item) === 0 ? styles.linkCountEmpty : ""
                    }`}
                  >
                    {linkedCount(item)}
                  </span>
                </button>
              )}
              <button
                title="Скачать файл"
                onClick={() => onDownload(multiple ? "offer" : "order", item)}
              >
                <Download size={17} />
              </button>
              <button
                className={styles.deleteAction}
                title="Удалить"
                onClick={() => onDelete(item)}
              >
                <Trash2 size={17} />
              </button>
            </div>
          </article>
        ))}
        {!items.length && <div className={styles.mobileEmpty}>Документов пока нет</div>}
      </div>
      <div className={styles.selectionSummary}>
        {multiple
          ? `Выбрано предложений: ${selected.length}`
          : selected.length
            ? "Запрос выбран"
            : "Выберите один запрос"}
      </div>
    </section>
  );
}

const DEFAULT_EMAIL_SUBJECT = "Запрос компонентов";
const DEFAULT_EMAIL_BODY = "Добрый день! Просим обработать запрос во вложении.";
const getDefaultEmailSubject = (order) =>
  order?.name ? `${DEFAULT_EMAIL_SUBJECT} — ${order.name}` : DEFAULT_EMAIL_SUBJECT;
const getDefaultEmailBody = (employeeName) =>
  employeeName
    ? `${DEFAULT_EMAIL_BODY}\n\nС уважением,\n${employeeName}`
    : DEFAULT_EMAIL_BODY;

function OrderEmailModal({ order, employeeName, onClose }) {
  const [employees, setEmployees] = useState([]);
  const [to, setTo] = useState([]);
  const [recipientQuery, setRecipientQuery] = useState("");
  const [subject, setSubject] = useState(DEFAULT_EMAIL_SUBJECT);
  const [body, setBody] = useState(getDefaultEmailBody(employeeName));
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    if (!order) return;

    setTo([]);
    setRecipientQuery("");
    setSubject(getDefaultEmailSubject(order));
    setBody(getDefaultEmailBody(employeeName));
    setLoadingEmployees(true);
    fetchEmployees()
      .then((rows) => {
        const seen = new Set();
        setEmployees(
          rows.filter((employee) => {
            const email = employee.email?.trim().toLowerCase();
            if (!email || seen.has(email)) return false;
            seen.add(email);
            return true;
          })
        );
      })
      .catch(() => {
        setEmployees([]);
        message.error("Не удалось загрузить адреса пользователей");
      })
      .finally(() => setLoadingEmployees(false));
  }, [employeeName, order]);

  const handleSend = async () => {
    if (!to.length || !subject.trim() || !body.trim()) {
      message.warning("Выберите получателей и заполните тему и текст письма");
      return;
    }

    setSending(true);
    try {
      await sendOrderEmail(order.id, {
        to,
        subject: subject.trim(),
        body: body.trim(),
        sendAsAuthenticatedUser: true,
      });
      message.success("Запрос отправлен по почте");
      onClose();
    } catch (error) {
      message.error(error.message || "Не удалось отправить письмо");
    } finally {
      setSending(false);
    }
  };
  const toggleRecipient = (email) => {
    setTo((current) =>
      current.includes(email)
        ? current.filter((item) => item !== email)
        : [...current, email]
    );
  };
  const normalizedRecipientQuery = recipientQuery.trim().toLowerCase();
  const filteredEmployees = normalizedRecipientQuery
    ? employees.filter((employee) =>
        `${employee.name || ""} ${employee.email || ""}`
          .toLowerCase()
          .includes(normalizedRecipientQuery)
      )
    : employees;

  return (
    <Modal
      open={Boolean(order)}
      width={900}
      title={`Отправить запрос «${order?.name || ""}»`}
      okText="Отправить"
      cancelText="Отмена"
      confirmLoading={sending}
      okButtonProps={{ disabled: loadingEmployees || !to.length || !subject.trim() || !body.trim() }}
      onOk={handleSend}
      onCancel={onClose}
      destroyOnHidden
      style={{ top: 24 }}
      styles={{
        body: {
          maxHeight: "calc(100vh - 190px)",
          overflowY: "auto",
          paddingRight: 6,
        },
      }}
    >
      <div className={styles.emailForm}>
        <div className={styles.emailFormField}>
          <span>Получатели</span>
          <input
            type="search"
            value={recipientQuery}
            onChange={(event) => setRecipientQuery(event.target.value)}
            placeholder="Найти по имени или email"
            disabled={loadingEmployees || employees.length === 0}
          />
          <div className={styles.recipientList}>
            {loadingEmployees && (
              <div className={styles.recipientStatus}>Загрузка адресов…</div>
            )}
            {!loadingEmployees && filteredEmployees.map((employee) => (
              <label className={styles.recipientOption} key={employee.id ?? employee.email}>
                <input
                  type="checkbox"
                  checked={to.includes(employee.email)}
                  onChange={() => toggleRecipient(employee.email)}
                />
                <span>
                  <strong>{employee.name || "Пользователь"}</strong>
                  <small>{employee.email}</small>
                </span>
              </label>
            ))}
            {!loadingEmployees && employees.length > 0 && filteredEmployees.length === 0 && (
              <div className={styles.recipientStatus}>Получатели не найдены</div>
            )}
          </div>
          {!loadingEmployees && employees.length === 0 && (
            <small>У пользователей не указаны email-адреса</small>
          )}
          {to.length > 0 && <small className={styles.selectedRecipients}>Выбрано: {to.length}</small>}
        </div>
        <label>
          <span>Тема</span>
          <input
            type="text"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            maxLength={255}
          />
        </label>
        <label>
          <span>Текст письма</span>
          <textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={4}
          />
        </label>
        <p>Файл запроса будет автоматически приложен в формате XLSX.</p>
      </div>
    </Modal>
  );
}

async function downloadDocument(type, item) {
  try {
    const response = await fetch(
      type === "order" ? `/api/getOriginFile/${item.id}` : `/api/getOriginFileOffer/${item.id}`
    );
    if (!response.ok) throw new Error();
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = `${item.name}_${type === "order" ? "order" : "offer"}.xlsx`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 200);
  } catch {
    message.error("Не удалось скачать файл");
  }
}

function deleteDocument(type, item, setItems, onDeleted) {
  Modal.confirm({
    title: `Удалить «${item.name}»?`,
    content: "Это действие необратимо.",
    okText: "Удалить",
    cancelText: "Отмена",
    okButtonProps: { danger: true },
    onOk: async () => {
      const options =
        type === "order"
          ? (() => {
              const body = new FormData();
              body.append("order", item.id);
              return { method: "POST", body };
            })()
          : {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ offer: item.id }),
            };
      const response = await fetch(
        type === "order" ? "/api/deleteOrder" : "/api/deleteOffer",
        options
      );
      if (!response.ok) throw new Error("Ошибка удаления");
      setItems?.((current) => current.filter((row) => row.id !== item.id));
      message.success("Документ удалён");
      try {
        await onDeleted?.();
      } catch {
        message.warning("Предложение удалено, но не удалось обновить счётчики заказов. Обновите страницу.");
      }
    },
  });
}

function Processing({ stages, stage }) {
  return (
    <section className={styles.processing}>
      <div className={styles.orbit}>
        <Sparkles size={28} />
        <span />
      </div>
      <h2>Анализируем предложения</h2>
      <p>Получаем данные из базы и сравниваем позиции</p>
      <div className={styles.steps}>
        {stages.map((label, index) => (
          <div className={`${styles.step} ${index <= stage ? styles.stepActive : ""}`} key={label}>
            {index < stage ? (
              <CheckCircle2 />
            ) : index === stage ? (
              <Loader2 className={styles.spin} />
            ) : (
              <span>{index + 1}</span>
            )}
            <strong>{label}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

function Results({ catalogLinks = false, result, orderId, offerIds, orderName, onBack, onReset }) {
  const [download, setDownload] = useState("");
  const [offersDetail, setOffersDetail] = useState(null);
  const [externalDetail, setExternalDetail] = useState(null);
  const [rowFilter, setRowFilter] = useState("all");
  const visibleMatches = result.matches.filter(
    (row) => rowFilter === "all" || (rowFilter === "matched" ? Boolean(row.best) : !row.best)
  );
  const unmatchedCount = result.matches.length - result.matched;
  const openExternalDocument = async (offer) => {
    if (offer.fileOffer?.id) {
      setOffersDetail(null);
      setExternalDetail({ ...offer.fileOffer, highlightRowId: offer.sourceOfferId });
      return;
    }
    try {
      const response = await fetch("/api/components/rows/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: offer.partNumber }),
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      const matchedRow = (Array.isArray(data?.offers) ? data.offers : []).find((row) =>
        offer.sourceOfferId
          ? String(row.id) === String(offer.sourceOfferId)
          : normalize(row.fileOffer?.name ?? row.file_offer?.name) === normalize(offer.supplier) &&
            normalizePriceText(row.price) === offer.priceText
      );
      const fileOffer = matchedRow?.fileOffer ?? matchedRow?.file_offer;
      if (!fileOffer?.id) throw new Error();
      setOffersDetail(null);
      setExternalDetail({ ...fileOffer, highlightRowId: matchedRow.id });
    } catch {
      message.error("Не удалось определить исходный файл предложения");
    }
  };
  const generate = async (url, kind) => {
    setDownload(kind);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offers: offerIds, order: orderId, orderName }),
      });
      if (!response.ok) throw new Error();
      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${orderName}_${kind}.xlsx`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
    } catch {
      message.error("Не удалось сформировать файл");
    } finally {
      setDownload("");
    }
  };
  return (
    <div className={styles.results}>
      <section className={styles.metrics}>
        <Metric
          icon={<ShieldCheck />}
          label="Сопоставлено"
          value={`${result.matched} из ${result.matches.length} позиций`}
        />
        <Metric icon={<Sparkles />} label="Лидер по ценам" value={result.leader} />
      </section>
      <section className={styles.reportActions}>
        <div>
          <strong>Серверные отчёты</strong>
          <span>Формируются существующим механизмом сравнения</span>
        </div>
        <button type="button" onClick={onBack}>
          <RefreshCw size={16} />
          Вернуться к выбору
        </button>
        <button type="button" onClick={onReset}>
          Новый выбор
        </button>
        <button
          disabled={Boolean(download)}
          onClick={() => generate("/api/generateReport", "comparison")}
        >
          <FileDown size={16} />
          {download === "comparison" ? "Формирование…" : "Сравнение цен"}
        </button>
        <button
          disabled={Boolean(download)}
          onClick={() => generate("/api/generateBestOffer", "best-offer")}
        >
          <Sparkles size={16} />
          {download === "best-offer" ? "Формирование…" : "Лучшее предложение"}
        </button>
      </section>
      <section className={styles.tableCard}>
        <div className={styles.tableHeading}>
          <div>
            <h3>Рекомендации по позициям</h3>
            <p>Показаны минимальная и максимальная найденные цены без округления</p>
          </div>
          <div className={styles.resultFilters}>
            <button
              className={rowFilter === "all" ? styles.resultFilterActive : ""}
              onClick={() => setRowFilter("all")}
            >
              Все <b>{result.matches.length}</b>
            </button>
            <button
              className={rowFilter === "matched" ? styles.resultFilterActive : ""}
              onClick={() => setRowFilter("matched")}
            >
              Сопоставленные <b>{result.matched}</b>
            </button>
            <button
              className={rowFilter === "unmatched" ? styles.resultFilterActive : ""}
              onClick={() => setRowFilter("unmatched")}
            >
              Не сопоставленные <b>{unmatchedCount}</b>
            </button>
          </div>
        </div>
        <div className={styles.tableWrap}>
          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Артикул</th>
                <th>Кол-во</th>
                <th>Источник</th>
                <th>Лучший поставщик</th>
                <th>Лучшая цена</th>
                <th>Срок поставки</th>
                <th>Срок годности</th>
                <th>Худший поставщик</th>
                <th>Худшая цена</th>
                <th>Предложений</th>
              </tr>
            </thead>
            <tbody>
              {visibleMatches.map((row) => {
                const externalBest = row.externalOffers
                  ?.slice()
                  .sort((a, b) => a.price - b.price)[0];
                const shownBest = row.best || externalBest;
                const shownCount = row.count || row.externalOffers?.length || 0;
                return (
                  <tr key={`${row.index}-${row.part}`}>
                    <td>{row.index}</td>
                    <td>
                      <strong>{row.part}</strong>
                    </td>
                    <td>{row.qty}</td>
                    <td>
                      <span
                        className={
                          row.best
                            ? styles.selectedSourceBadge
                            : row.externalOffers?.length
                              ? styles.externalSourceBadge
                              : styles.noSourceBadge
                        }
                      >
                        {row.best
                          ? "Выбранные предложения"
                          : row.externalOffers?.length
                            ? "Из общей базы"
                            : row.externalLookupFailed
                              ? "Ошибка поиска в базе"
                              : "Не найдено"}
                      </span>
                    </td>
                    <td>
                      {shownBest ? (
                        <div className={styles.supplierSource}>
                          <strong>{shownBest.supplier}</strong>
                          {!row.best && <span>Из других предложений</span>}
                        </div>
                      ) : (
                        "Нет предложения"
                      )}
                    </td>
                    <td className={styles.price}>{shownBest?.priceText || "—"}</td>
                    <td>{shownBest?.leadTime || "—"}</td>
                    <td>{shownBest?.shelfLife || "—"}</td>
                    <td>{row.worst?.supplier || "—"}</td>
                    <td className={styles.worstPrice}>{row.worst?.priceText || "—"}</td>
                    <td>
                      <button
                        className={shownCount ? styles.offerCountButton : styles.offerCountEmpty}
                        disabled={!shownCount}
                        onClick={() =>
                          setOffersDetail(
                            row.best ? row : { ...row, offers: row.externalOffers, external: true }
                          )
                        }
                      >
                        {shownCount ? `${shownCount}${!row.best ? " из базы" : ""}` : "Нет"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!visibleMatches.length && (
            <div className={styles.noFilteredRows}>В этой группе строк нет</div>
          )}
        </div>
      </section>
      {offersDetail && (
        <OffersDetail
          row={offersDetail}
          onClose={() => setOffersDetail(null)}
          onOpenDocument={openExternalDocument}
        />
      )}
      <DetailOffer
        catalogLinks={catalogLinks}
        isOpen={Boolean(externalDetail)}
        selectedFileIds={externalDetail?.id ? [externalDetail.id] : []}
        numberOffer={externalDetail?.name}
        highlightRowId={externalDetail?.highlightRowId}
        onClose={() => setExternalDetail(null)}
      />
    </div>
  );
}

function OffersDetail({ row, onClose, onOpenDocument }) {
  const maxPrice = Math.max(...row.offers.map((item) => item.price), 1);
  return (
    <div className={styles.detailBackdrop} onClick={onClose}>
      <section className={styles.offersDetail} onClick={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span>{row.external ? "Из других предложений" : "Все предложения"}</span>
            <h3>{row.part}</h3>
            <p>{row.offers.length} предложений · от лучшей цены к худшей</p>
          </div>
          <button type="button" data-modal-close onClick={onClose} aria-label="Закрыть">×</button>
        </header>
        <div className={styles.priceChart}>
          {row.offers.map((offer, index) => (
            <div className={styles.chartRow} key={`${offer.supplier}-${index}`}>
              <div>
                <strong>{offer.supplier}</strong>
                <span>{offer.priceText}</span>
              </div>
              <div className={styles.chartTrack}>
                <span
                  className={index === 0 ? styles.bestBar : ""}
                  style={{ width: `${Math.max(5, (offer.price / maxPrice) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className={styles.detailTable}>
          <table>
            <thead>
              <tr>
                <th>Место</th>
                <th>Поставщик</th>
                <th>Цена</th>
                <th>Срок поставки</th>
                <th>Срок годности</th>
                {row.external && <th>Документ</th>}
              </tr>
            </thead>
            <tbody>
              {row.offers.map((offer, index) => (
                <tr key={`${offer.supplier}-${offer.price}-${index}`}>
                  <td>
                    {index === 0 ? <span className={styles.bestLabel}>Лучшее</span> : index + 1}
                  </td>
                  <td>
                    <strong>{offer.supplier}</strong>
                  </td>
                  <td>{offer.priceText}</td>
                  <td>{offer.leadTime || "—"}</td>
                  <td>{offer.shelfLife || "—"}</td>
                  {row.external && (
                    <td>
                      <button className={styles.documentLink} onClick={() => onOpenDocument(offer)}>
                        Открыть предложение
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Metric({ icon, label, value }) {
  return (
    <div className={styles.metric}>
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

async function downloadGeneratedReport(url, kind, offerIds, orderId, orderName) {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ offers: offerIds, order: orderId, orderName }),
  });
  if (!response.ok) throw new Error("Report generation failed");
  const blobUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `${orderName}_${kind}.xlsx`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
}

async function fetchRecentOffersBatch(partNumbers, limit) {
  const result = new Map(partNumbers.map((part) => [normalize(part), []]));
  if (!partNumbers.length) return { offers: result, failed: false };
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch("/api/components/recent-offers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ part_numbers: partNumbers, limit }),
      signal: controller.signal,
    });
    if (!response.ok) {
      const details = await response.text().catch(() => "");
      console.error("Ошибка /api/components/recent-offers", {
        status: response.status,
        request: { part_numbers: partNumbers, limit },
        response: details,
      });
      return { offers: result, failed: true };
    }

    const data = await response.json();
    const payload =
      data?.offersByPartNumber ??
      data?.offers_by_part_number ??
      data?.results ??
      data?.offers ??
      data?.items ??
      data?.data ??
      data;
    const entries = Array.isArray(payload)
      ? payload.map((group) => [
          group.partNumber ??
            group.part_number ??
            group.requestedPartNumber ??
            group.requested_part_number ??
            group.cleanPartNumber ??
            group.clean_part_number ??
            group.name,
          group.recentOffers ??
            group.recent_offers ??
            group.offers ??
            group.items ??
            group.list ??
            [],
        ])
      : payload && typeof payload === "object"
        ? Object.entries(payload)
        : [];

    entries.forEach(([part, rows]) => {
      if (!part || !Array.isArray(rows)) return;
      result.set(
        normalize(part),
        rows
          .slice(0, limit)
          .map((row) => normalizeRecentOffer(row, part))
          .filter(Boolean)
      );
    });
  } catch (error) {
    console.warn("Дополнительные предложения временно недоступны", error);
    return { offers: result, failed: true };
  } finally {
    clearTimeout(timeoutId);
  }
  return { offers: result, failed: false };
}

function normalizeRecentOffer(row, fallbackPartNumber) {
  const sourceOfferId = row.offerId ?? row.offer_id ?? null;
  const fileOfferId =
    row.fileOfferId ??
    row.file_offer_id ??
    row.offerFileId ??
    row.offer_file_id ??
    row.fileOffer?.id;
  const offerName =
    row.offerName ??
    row.offer_name ??
    row.fileOfferName ??
    row.file_offer_name ??
    row.offerFileName ??
    row.offer_file_name ??
    row.fileOffer?.name ??
    row.supplier ??
    "Предложение";
  const price = toNumber(row.price);
  return {
    supplier: offerName,
    price,
    priceText: normalizePriceText(row.price),
    leadTime: row.leadTime ?? row.lead_time ?? row.lt ?? "—",
    shelfLife: row.dateCode ?? row.date_code ?? row.dc ?? row.shelfLife ?? row.shelf_life ?? "—",
    partNumber:
      row.partNumber ??
      row.part_number ??
      row.name ??
      row.cleanPartNumber ??
      row.clean_part_number ??
      fallbackPartNumber,
    sourceOfferId,
    fileOffer: fileOfferId ? { id: fileOfferId, name: offerName } : (row.fileOffer ?? null),
  };
}
const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(value))
    : "—";
const normalizePriceText = (value) => {
  const text = String(value ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(",", ".");
  return /^-?\d+(\.\d+)?$/.test(text) ? text : String(toNumber(value));
};
